// Thin API layer for the reporting page. Every network call the report makes
// lives here so components stay declarative and the endpoints are easy to see
// in one glance.

import { base_url, get_data } from "../Geoportal";

const REPORTING_STATS = `${base_url}/api/data_api/geojson/get_reporting_statistics/`;
const ADMIN_ONE_LIST = `${base_url}/api/data_api/geojson/admin_one_list/`;
const GEOJSON_INFO = `${base_url}/api/data_api/geojson/getgeojsoninfo/`;
const REPORT_MAP = `${base_url}/api/data_api/reporting/map/`;
const METADATA = `${base_url}/api/metadata/`;

/**
 * Fetch one reporting-statistics dataset for a country/time step.
 *
 * @param {object} params
 * @param {string} params.dataParam Dataset key (e.g. "cdi", "spi", "cdi_overview").
 * @param {string|number} params.countryId Numeric country code.
 * @param {string|number} params.year Four-digit year.
 * @param {string|number} params.month Month as an integer (1-12).
 * @param {string} params.dekad Dekad day ("01" | "11" | "21").
 * @returns {Promise<object>} Raw JSON payload (shape depends on dataParam).
 */
export async function fetchReportingStat({ dataParam, countryId, year, month, dekad }) {
  const qs = new URLSearchParams({
    year: String(year),
    month: String(month),
    dekad: String(dekad),
    country_id: String(countryId),
  });
  if (dataParam) qs.set("data", dataParam);
  return get_data(`${REPORTING_STATS}?${qs.toString()}`);
}

/**
 * List the admin-1 units for a country (for the "Admin One selection" dropdown).
 *
 * @param {string|number} countryId Numeric country code.
 * @returns {Promise<Array<{admin_one_name: string, admin_one_code: number, admin_one_bounds: object}>>}
 */
export async function fetchAdminOneUnits(countryId) {
  const payload = await get_data(`${ADMIN_ONE_LIST}?country_id=${countryId}`);
  return payload?.admin_one_units || [];
}

/**
 * All African countries (name, numeric code, bounds), sorted by name. Used by
 * the National reporting-unit dropdown to switch the report to another country.
 *
 * @returns {Promise<Array<{code: number, name: string, bounds: object|null}>>}
 */
export async function fetchCountries() {
  const payload = await get_data(`${GEOJSON_INFO}?country_names=ALL`);
  const regions = payload?.regions_info || {};
  const seen = new Set();
  const countries = [];
  Object.values(regions).forEach((region) => {
    (region?.country_code || []).forEach((c) => {
      if (c?.country_code == null || seen.has(c.country_code)) return;
      seen.add(c.country_code);
      countries.push({
        code: c.country_code,
        name: c.country_name,
        bounds: c.country_bounds || null,
      });
    });
  });
  return countries.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Look up a composited report map PNG (same Cartopy render the chatbot uses).
 *
 * @param {object} params
 * @param {string} params.indicator LAYER_CONFIG key (cdi, spi, spi_era5, sma, fapar_anomaly).
 * @param {string|number} params.year
 * @param {string|number} params.month Month as an integer 1-12.
 * @param {number} [params.dekad] 1 | 11 | 21.
 * @param {number} [params.timescale] Accumulation period for SPI.
 * @param {string|number} params.countryId Numeric country code.
 * @returns {Promise<{url?: string, ready: boolean}>}
 */
export async function fetchReportMap({ indicator, year, month, dekad, timescale, countryId }) {
  const qs = new URLSearchParams({
    indicator,
    year: String(year),
    month: String(Number(month)),
  });
  if (dekad != null) qs.set("dekad", String(dekad));
  if (timescale != null) qs.set("timescale", String(timescale));
  if (countryId != null) qs.set("country_id", String(countryId));
  return get_data(`${REPORT_MAP}?${qs.toString()}`);
}

/**
 * Fetch product metadata and reduce it to `{ dataset_id: overview_text }`.
 *
 * @returns {Promise<Record<string, string>>}
 */
export async function fetchProductOverviews() {
  const datasets = await get_data(METADATA);
  if (!Array.isArray(datasets)) return {};
  return datasets.reduce((acc, d) => {
    if (d?.dataset_id) acc[d.dataset_id] = d.overview;
    return acc;
  }, {});
}
